/*
  np-bfcm.js — BFCM storefront helpers (Viktor's CRO brief, Nov 2026).
  Loaded once, deferred, from layout/theme.liquid.

  1. Countdown ([data-np-bfcm-cd], snippets/np-bfcm-countdown): each root holds
     one hidden [data-np-cd-offer] row per offer (name + end in epoch ms). Every
     second, show the first offer whose end is still ahead as
     "<name> ends in 02d 14h 31m 05s"; when it passes, move on to the next one;
     after the last, hide the root. Ends are absolute instants (AEDT in the
     theme settings), so every country counts down to the same moment.

  2. Badge end date ([data-np-bfcm-end], snippets/np-bfcm-badge, the np-tps
     cards and the PDP image badge's <style> in theme.liquid): hide (or, for
     the <style>, remove) each one once its end time has passed, so a page
     served from cache after 1 Dec 23:59 AEDT doesn't keep showing it.

  3. PDP image badge: while one-time is the selected option in the PDP buy
     box, <html> gets .np-bfcm-otp-on and the gallery badge (a ::after in
     theme.liquid) shows the 20% sticker instead of the "up to X%" one. Off
     again when a subscription is selected. The quick-add popup's widget is
     ignored.

  4. Buy-box plan pills (PDP + quick-add popup): during BFCM the 30-day pill
     reads "Save X% on your first 3 orders" and the 90-day one adds
     " (9 months)". The widget's own JS rewrites these pills in several places
     (variant change, BFCM pricing, idea6/legacy copies), so a MutationObserver
     puts the wording back straight after any rewrite. X is read from the
     pill itself, so it follows the widget's maths.

  5. 90-day card prize banner: during BFCM a copy of the card's prize-draw
     row (.np-free-travel-support) is placed where the "+ Free Shipping
     Forever / + Win…" lines (.np-ab-plus) sit on single PDPs, or under the
     per-day / delivery line on bundle PDPs and in the quick-add popup, so the prize shows even when
     90-day isn't selected. CSS (np-bfcm.css) hides the plus
     lines and shows the copy only while the card isn't selected.

  6. PDP image sticker on the main image only: when the page loads and each
     time a purchase option is picked, the gallery jumps to that option's
     main image. That slide's media id is remembered and its slides (desktop
     + mobile carousels) get .np-bfcm-main; the sticker (theme.liquid) shows
     only there, not on the other images as shoppers swipe through.

  7. Bundle buy box (PDP + popup, [data-np-bfcm-bundle]): follows the BFCM
     pricing sheet ("1 Nov-1 Dec Hero" tab). One-time: struck against
     compare-at, pill = % off compare-at (also set as data-np-bfcm-otp-save
     for the sticky bar). Pills and the "first 3 orders"
     bullets show the bundle's % off summed retail (data-np-bfcm-sub,
     40/39/38/36), the struck price is compare-at × months
     (data-np-bfcm-cmp × 1 or 3), and the "every order after" bullet is the
     recurring price's % off that. Sale prices stay as the plan gives them.

  8. Quick-add popup image sticker: the popup has no theme.liquid gallery
     badge, so a sticker <img> is placed top-left over its media
     (.np-tps-modal__media) — the subscription % sticker, or the 20% one
     while one-time is selected — only while the option's main image is the
     slide showing. URLs come from the widget
     (data-np-bfcm-sticker / -otp, snippets/np-bfcm-sticker).

  Re-scans the DOM each tick, so roots added later (quick-buy modal, AJAX
  filtering on Shop All, cart drawer recs) are picked up without extra hooks.
*/
(function () {
  'use strict';

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function format(ms) {
    var s = Math.max(0, Math.floor(ms / 1000));
    var d = Math.floor(s / 86400);
    var h = Math.floor((s % 86400) / 3600);
    var m = Math.floor((s % 3600) / 60);
    return pad(d) + 'd ' + pad(h) + 'h ' + pad(m) + 'm ' + pad(s % 60) + 's';
  }

  function tickCountdowns(now) {
    document.querySelectorAll('[data-np-bfcm-cd]').forEach(function (root) {
      var live = null;
      root.querySelectorAll('[data-np-cd-offer]').forEach(function (row) {
        var end = Number(row.getAttribute('data-end'));
        if (!live && end > now) live = { name: row.getAttribute('data-name'), end: end };
      });
      if (!live) {
        root.hidden = true;
        return;
      }
      var nameEl = root.querySelector('[data-np-cd-name]');
      var timeEl = root.querySelector('[data-np-cd-time]');
      if (nameEl && nameEl.textContent !== live.name) nameEl.textContent = live.name;
      if (timeEl) timeEl.textContent = format(live.end - now);
      root.hidden = false;
    });
  }

  function hideEndedBadges(now) {
    document.querySelectorAll('[data-np-bfcm-end]').forEach(function (el) {
      var end = Number(el.getAttribute('data-np-bfcm-end'));
      if (!end || now < end) return;
      // The PDP image badge is a <style> block (layout/theme.liquid): drop it.
      if (el.tagName === 'STYLE') el.remove();
      else el.hidden = true;
    });
  }

  function syncImageBadge() {
    var widget = document.querySelector('.np-sub-widget[data-np-bfcm]:not(.np-tps-modal *)');
    var otp = !!(widget && widget.querySelector('.np-card[data-purchase="onetime"].np-active'));
    var root = document.documentElement;
    if (root.classList.contains('np-bfcm-otp-on') !== otp) root.classList.toggle('np-bfcm-otp-on', otp);
  }

  function planPillText(card, pill) {
    var m = (pill.textContent || '').match(/save\s*(\d+)\s*%/i);
    var pct = m ? m[1] : card.getAttribute('data-savings-pct');
    var w = card.closest('.np-sub-widget');
    if (w && w.hasAttribute('data-np-bfcm-bundle') && w.getAttribute('data-np-bfcm-sub')) {
      pct = w.getAttribute('data-np-bfcm-sub');
    }
    if (!pct || pct === '0') return null;
    var title = card.querySelector('.np-card-title');
    var is90 = /90/.test(title ? title.textContent : '');
    return 'Save ' + pct + '% on your first 3 orders' + (is90 ? ' (9 months)' : '');
  }

  function syncPlanPills() {
    document.querySelectorAll('.np-sub-widget[data-np-bfcm] .np-card[data-purchase="subscription"] .np-badge').forEach(function (pill) {
      var card = pill.closest('.np-card');
      var text = planPillText(card, pill);
      if (!text) return;
      var parts = pill.querySelectorAll(':scope > .np-v2-only, :scope > .np-v2-legacy-only');
      if (!parts.length) parts = [pill];
      parts.forEach(function (el) {
        if (el.textContent !== text) el.textContent = text;
      });
    });
  }

  function moneyFmt(widget, cents) {
    var probe = widget.querySelector('[data-np-money-format-probe]');
    var tpl = probe ? probe.textContent.trim() : '$1,234.56';
    var mm = tpl.match(/1(\D?)234(\D)56/);
    var ts = mm ? mm[1] : ',';
    var ds = mm ? mm[2] : '.';
    var parts = (cents / 100).toFixed(2).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ts);
    return mm ? tpl.replace(mm[0], whole + ds + parts[1]) : '$' + whole + '.' + parts[1];
  }

  function setLabel(el, text) {
    if (!el) return;
    if (!el.querySelector('*:not(svg):not(path):not(circle)')) {
      var nodes = [];
      el.childNodes.forEach(function (n) {
        if (n.nodeType === 3 && n.textContent.trim()) nodes.push(n);
      });
      if (nodes.length) {
        if (nodes[nodes.length - 1].textContent.trim() !== text) nodes[nodes.length - 1].textContent = text;
        return;
      }
    }
    if (el.textContent.trim() !== text) el.textContent = text;
  }

  function syncBundleBuyBox() {
    document.querySelectorAll('.np-sub-widget[data-np-bfcm][data-np-bfcm-bundle]').forEach(function (widget) {
      var sub = parseInt(widget.getAttribute('data-np-bfcm-sub'), 10);
      var cmp = parseInt(widget.getAttribute('data-np-bfcm-cmp'), 10);
      if (!sub || !cmp) return;
      // One-time: struck against compare-at, % off compare-at (the sheet's
      // "Hero one-time" badge, e.g. Turbo 31%, Ultimate 29%).
      var one = widget.querySelector('.np-card[data-purchase="onetime"]');
      var onePrice = one ? parseInt(one.getAttribute('data-plan-month-price'), 10) : 0;
      if (one && onePrice > 0 && onePrice < cmp) {
        // The sheet's badge comes from the nominal ladder, so it can differ by
        // 1 from the price maths (Anabolic: 25.5% → sheet 26%). Use the sheet's
        // figure, keyed off the bundle's sub % (36/38/39/40 → 24/26/27/29;
        // Turbo, the 14% ladder bundle, 31).
        var handle = widget.getAttribute('data-product-handle') || '';
        var onePct = /turbo/.test(handle) ? 31 : ({ 36: 24, 38: 26, 39: 27, 40: 29 })[sub] ||
          Math.round(((cmp - onePrice) * 100) / cmp);
        var oneWas = one.querySelector('[data-onetime-was-price]');
        if (oneWas) {
          var oneWasTxt = moneyFmt(widget, cmp);
          if (oneWas.textContent.trim() !== oneWasTxt) oneWas.textContent = oneWasTxt;
        }
        var oneText = 'BLACK FRIDAY \u00b7 SAVE ' + onePct + '%';
        one.querySelectorAll('.np-badge > span').forEach(function (el) {
          if (el.textContent !== oneText) el.textContent = oneText;
        });
        if (widget.getAttribute('data-np-bfcm-otp-save') !== String(onePct)) widget.setAttribute('data-np-bfcm-otp-save', String(onePct));
      }
      widget.querySelectorAll('.np-card[data-purchase="subscription"]').forEach(function (card) {
        var title = card.querySelector('.np-card-title');
        var months = /90/.test(title ? title.textContent : '') ? 3 : 1;
        var was = cmp * months;
        var wasEl = card.querySelector('[data-card-was-price]');
        if (wasEl) {
          var wasTxt = moneyFmt(widget, was);
          if (wasEl.textContent.trim() !== wasTxt) wasEl.textContent = wasTxt;
          if (wasEl.hasAttribute('hidden')) wasEl.removeAttribute('hidden');
        }
        if (card.getAttribute('data-savings-pct') !== String(sub)) card.setAttribute('data-savings-pct', String(sub));
        setLabel(
          card.querySelector(months === 3 ? '[data-benefit-first]' : '[data-monthly-benefit-first]'),
          sub + '% off your first 3 orders' + (months === 3 ? ' (9 months)' : '')
        );
        var money = card.querySelectorAll('.np-billing-note [data-np-money]');
        var rec = money.length ? parseInt(money[money.length - 1].getAttribute('data-np-money'), 10) : 0;
        if (rec > 0 && rec < was) {
          setLabel(
            card.querySelector(months === 3 ? '[data-benefit-discount]' : '[data-monthly-benefit-discount]'),
            Math.round(((was - rec) * 100) / was) + '% off every order after the bundle price'
          );
        }
      });
    });
  }

  var pillTimer = null;
  new MutationObserver(function () {
    if (pillTimer) return;
    pillTimer = setTimeout(function () {
      pillTimer = null;
      syncPlanPills();
      syncBundleBuyBox();
    }, 0);
  }).observe(document.documentElement, { childList: true, characterData: true, subtree: true });

  function syncPrizePeek() {
    // PDP singles (idea6): where the "+ Free Shipping / + Win…" lines sit.
    // Bundle PDPs and the quick-add popup have no plus lines, so under the
    // 90-day card's per-day / delivery line.
    var anchors = [];
    document.querySelectorAll('.np-sub-widget[data-np-bfcm] .np-ab-plus').forEach(function (plus) {
      anchors.push(plus);
    });
    // Quick-add popup and the bundle PDP layout have no plus lines.
    document.querySelectorAll('.np-sub-widget[data-np-bfcm] .np-card').forEach(function (card) {
      if (card.querySelector('.np-ab-plus') || !card.querySelector('.np-card-body .np-bfcm-prize')) return;
      var at = card.querySelector(':scope > .np-card-meta') || card.querySelector(':scope > .np-v2-delivery');
      if (at) anchors.push(at);
    });
    anchors.forEach(function (anchor) {
      var card = anchor.closest('.np-card');
      if (!card) return;
      var peek = card.querySelector('.np-bfcm-prize-peek');
      if (!peek) {
        var row = card.querySelector('.np-card-body .np-free-travel-support');
        if (!row || !row.querySelector('.np-bfcm-prize')) return;
        peek = row.cloneNode(true);
        peek.classList.add('np-bfcm-prize-peek');
        peek.setAttribute('aria-hidden', 'true');
      }
      if (peek.previousElementSibling !== anchor) anchor.insertAdjacentElement('afterend', peek);
    });
  }

  var mainOptKey = null;
  var mainSettle = 0;
  var mainId = null;

  function syncMainSlide() {
    var slides = document.querySelectorAll('.product-media-carousel__slide, .product-media-carousel-mobile__slide');
    if (!slides.length) return;
    var widget = document.querySelector('.np-sub-widget:not(.np-tps-modal *)');
    var active = widget && widget.querySelector('.np-card.np-active');
    var key = active ? (active.getAttribute('data-purchase') || '') + '|' + (active.querySelector('.np-card-title') || active).textContent.trim() : '';
    if (key !== mainOptKey) {
      mainOptKey = key;
      mainSettle = 3; // let the gallery finish jumping before freezing the choice
    }
    if (mainSettle > 0) {
      mainSettle--;
      var sel = document.querySelector('.product-media-carousel__slide.is-selected') ||
        document.querySelector('.product-media-carousel__slide') || slides[0];
      mainId = sel && sel.getAttribute('data-media-id');
    }
    if (!mainId) return;
    slides.forEach(function (s) {
      var on = s.getAttribute('data-media-id') === mainId;
      if (s.classList.contains('np-bfcm-main') !== on) s.classList.toggle('np-bfcm-main', on);
    });
    document.documentElement.classList.add('np-bfcm-main-ready');
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.np-sub-widget .np-card')) {
      setTimeout(syncMainSlide, 250);
    }
  });

  // Popup sticker shows only on the option's main image: when an option is
  // picked (or the popup opens) the gallery scrolls to that option's image;
  // once it settles, that slide is remembered and the sticker hides while the
  // shopper swipes to any other slide.
  var modalKey = null;
  var modalSettle = 0;
  var modalMain = null;

  function modalVisibleSlide(modal) {
    var media = modal.querySelector('.np-tps-modal__media');
    if (!media) return null;
    var left = media.getBoundingClientRect().left;
    var best = null;
    var bestD = Infinity;
    modal.querySelectorAll('.np-tps-modal__slide').forEach(function (s) {
      var d = Math.abs(s.getBoundingClientRect().left - left);
      if (d < bestD) {
        bestD = d;
        best = s;
      }
    });
    return best;
  }

  function syncModalSticker() {
    document.querySelectorAll('.np-tps-modal').forEach(function (modal) {
      var media = modal.querySelector('.np-tps-modal__media');
      var widget = modal.querySelector('.np-sub-widget[data-np-bfcm-sticker]');
      var img = media && media.querySelector('.np-bfcm-modal-sticker');
      if (!media || !widget) {
        if (img) img.remove();
        modalKey = null;
        return;
      }
      var active = widget.querySelector('.np-card.np-active');
      var otp = active && active.getAttribute('data-purchase') === 'onetime';
      var src = otp ? widget.getAttribute('data-np-bfcm-sticker-otp') : widget.getAttribute('data-np-bfcm-sticker');
      if (!src) {
        if (img) img.remove();
        return;
      }
      if (!img) {
        img = document.createElement('img');
        img.className = 'np-bfcm-sticker np-bfcm-modal-sticker';
        img.alt = '';
        img.setAttribute('aria-hidden', 'true');
        media.appendChild(img);
      }
      if (img.getAttribute('src') !== src) img.setAttribute('src', src);

      var key = widget.getAttribute('data-product-id') + '|' + (active ? Array.prototype.indexOf.call(widget.querySelectorAll('.np-card'), active) : '');
      if (key !== modalKey) {
        modalKey = key;
        modalSettle = 2;
      }
      var vis = modalVisibleSlide(modal);
      if (modalSettle > 0) {
        modalSettle--;
        modalMain = vis;
      }
      var show = !!vis && vis === modalMain;
      if (img.hidden === show) img.hidden = !show;
    });
  }

  document.addEventListener('scroll', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('np-tps-modal__gallery') && modalSettle === 0) syncModalSticker();
  }, true);

  function tick() {
    var now = Date.now();
    tickCountdowns(now);
    hideEndedBadges(now);
    syncImageBadge();
    syncPlanPills();
    syncPrizePeek();
    syncMainSlide();
    syncBundleBuyBox();
    syncModalSticker();
  }

  tick();
  setInterval(tick, 1000);
})();
